import React, { FC, useEffect, useMemo, useState } from 'react';
import NextLink from 'next/link';
import { useParams, useRouter } from 'next/navigation';
import { Product } from 'shared/types/product/Product';
import { ITEM_CODE, ITEM_NAME } from 'helpers/constants/attributes';
import { localeMapping } from 'helpers/constants/localeMapping';
import { sessionStorageKeys } from 'helpers/constants/sessionStorageKeys';
import { trackEvent } from 'helpers/customEvents';
import useWindowWidth from 'helpers/hooks/useBrowerWidth';
import usePath from 'helpers/hooks/usePath';
import { getPdpUrl } from 'helpers/utils/pdpUrlHelper';
import Image from 'frontastic/lib/image';
import ShopOnline from '../shop-online';
import ColorSwatchesDropdown from './colorswatchesdropdown';
import ColorSwatches from './swatches';

/**
 * - `grid`    — the listing card: V2 PLP, /search, "Algolia Query" slider and grid, PDP related products.
 *               Image carousel, RRP line (hidden for builder-range products), variant dropdown.
 * - `list`    — the list-view row of the V2 PLP and /search (their grid/list toggle).
 * - `compact` — the "Product IDs" slider and grid card: single image, colour circles, no RRP.
 */
export type ProductTileLayout = 'grid' | 'list' | 'compact';

interface ProductTileProps {
  product: Product;
  layout: ProductTileLayout;
  /** Appends `?sr=1` to the PDP link (a /search result). */
  isSearchResult?: boolean;
  /** Collection PLP: white card background, and the collection is remembered for the PDP breadcrumb. */
  isCollectionPage?: boolean;
  /** Category PLP / search: the category is remembered for the PDP breadcrumb. */
  isCategoryPage?: boolean;
}

const PLACEHOLDER_IMAGE = '/images/no-image-placeholder/product-thumbnail.jpg';
const ERROR_IMAGE = '/images/no-image-placeholder/search-result-thumbnail.png';

const onImageError = (event: any) => {
  event.target.id = ERROR_IMAGE;
  event.target.srcset = ERROR_IMAGE;
};

/** The variant's RRP in the market currency, e.g. `657.00` — '' when it has none. */
const rrpOf = (variant: any, locale: string): string => {
  const value = variant?.prices?.[locale === 'au' ? 'AUD' : 'NZD']?.priceValues?.[0]?.value;
  return value ? (value / 100).toFixed(2) : '';
};

/** Builder-range products never show an RRP (the backend also strips their prices). */
const isBuilderRange = (product: Product, variant: any): boolean => {
  const raw = variant?.attributes?.['builder-range'] || (product as any)?.attributesAlgolia?.['builder-range'];
  // Product-level builder-range is localized (`{ "en-AU": "No" }`).
  const value = typeof raw === 'object' && raw !== null ? raw.label || raw.key || Object.values(raw)[0] : raw;
  return value === true || (typeof value === 'string' && value.trim().toLowerCase() === 'yes');
};

/**
 * THE product tile — every product listing on both paths renders this one component (see `layout`).
 * Takes a Product built by helpers/algolia/map-product from an Algolia hit.
 */
const ProductTile: FC<ProductTileProps> = ({
  product,
  layout,
  isSearchResult = false,
  isCollectionPage = false,
  isCategoryPage = false,
}) => {
  const { locale } = useParams();
  const localeKey = localeMapping[locale];
  const router = useRouter();
  const { path } = usePath();
  const width = useWindowWidth();

  const [selectedVariant, setSelectedVariant] = useState(
    () =>
      product?.selectedVariant ||
      product?.variants?.filter((variant) => variant.id === product?.masterVariantID)?.[0] ||
      product.variants?.[0],
  );
  // A new selected variant from the listing (e.g. a colour filter) replaces the tile's own choice.
  useEffect(() => {
    if (product?.selectedVariant) setSelectedVariant(product.selectedVariant);
  }, [product?.selectedVariant?.id, product?.selectedVariant?.sku, product?.selectedVariant]);

  const images = selectedVariant?.images && selectedVariant.images.length > 0 ? selectedVariant.images : [PLACEHOLDER_IMAGE];
  const [currentIndex, setCurrentIndex] = useState(0);
  useEffect(() => {
    setCurrentIndex(0);
  }, [selectedVariant?.id, selectedVariant?.sku]);
  const prevImage = () => setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  const nextImage = () => setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));

  const [imageHovered, setImageHovered] = useState(false);

  const productUrl = useMemo(() => {
    const pUrl = product._url || getPdpUrl(product.slug, selectedVariant?.sku, selectedVariant?.attributes?.colour);
    return pUrl + (isSearchResult ? '?sr=1' : '');
  }, [product, selectedVariant, isSearchResult]);

  const trackProductClick = () => {
    const productId = product.productId || product.productcode || selectedVariant?.sku || product.slug || selectedVariant?.id || '';
    trackEvent('product_click', { productId, name: product.name, sku: selectedVariant?.sku });
  };

  // Card click: track, then navigate ourselves so the PLP can be remembered for the PDP breadcrumb.
  // Cmd/Ctrl/Shift-click opens a new tab; Alt-click is left to the browser.
  const handleTileClick = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    trackProductClick();
    if (e.altKey) return;
    e.preventDefault();
    e.stopPropagation();
    if (e.metaKey || e.ctrlKey || e.shiftKey) {
      const destinationUrl = isCategoryPage
        ? productUrl + '?category=' + path
        : isCollectionPage
        ? productUrl + '?collection=' + path
        : productUrl;
      window.open(`/${locale}${destinationUrl}`, '_blank');
    } else {
      sessionStorage.setItem(sessionStorageKeys.categoryLink, '');
      sessionStorage.setItem(sessionStorageKeys.collectionLink, '');
      if (isCategoryPage) sessionStorage.setItem(sessionStorageKeys.categoryLink, path);
      else if (isCollectionPage) sessionStorage.setItem(sessionStorageKeys.collectionLink, path);
      router.push(`/${locale}${productUrl}`);
    }
  };

  const categoryLabel =
    product?.categories &&
    (product?.categories[0]?.name || product?.categories?.[1]?.categoryId || product?.categories[0]?.categoryId);
  const name = product?.attributesAlgolia?.[ITEM_NAME]?.[localeKey] || product?.name;
  const productCode = selectedVariant?.attributes?.[ITEM_CODE] || product?.attributesAlgolia?.[ITEM_CODE];
  const showRrp = !isBuilderRange(product, selectedVariant);
  const cardBackground = imageHovered ? 'bg-[#F3EDE9]' : isCollectionPage ? 'bg-white' : 'bg-[#F8F8F8]';
  const stopLink = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  return (
    <div>
      <NextLink href={`/${locale}${productUrl}`} prefetch={true} onClick={trackProductClick}>
        {/* GRID
        --------------------- */}
        {layout === 'grid' && (
          <div onClick={handleTileClick}>
            <div className="relative">
              <div className="relative w-full" onMouseEnter={() => setImageHovered(true)} onMouseLeave={() => setImageHovered(false)} >
                <div className="relative block w-full xl:h-[10rem] 2xl:h-[23rem] bg-white" style={{ paddingBottom: '100%' }}>
                  <Image
                    priority
                    src={encodeURI(images[currentIndex].toString())}
                    alt={product.name ?? ''}
                    style={{ objectFit: 'cover', objectPosition: 'center' }}
                    className="w-full rounded-sm group-hover:opacity-75 "
                    fill
                    sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                    onError={onImageError}
                  />

                  {images.length > 1 && (
                    <button
                      onClick={(e) => {
                        stopLink(e);
                        prevImage();
                      }}
                      className="absolute top-1/2 left-2 transform -translate-y-1/2 bg-gray text-black p-2 rounded-full text-xl"
                      aria-label="Previous image"
                      style={{ lineHeight: 1 }}
                    >
                      &lt;
                    </button>
                  )}

                  {images.length > 1 && (
                    <button
                      onClick={(e) => {
                        stopLink(e);
                        nextImage();
                      }}
                      className="absolute top-1/2 right-2 transform -translate-y-1/2 bg-gray text-black p-2 rounded-full text-xl"
                      aria-label="Next image"
                      style={{ lineHeight: 1 }}
                    >
                      &gt;
                    </button>
                  )}
                </div>
              </div>
            </div>

            <div className={`h-[12rem] ${cardBackground} ] 2xl:px-8 xl:px-8 lg:px-8 md:px-8 px-16 pt-16 hover:bg-[#F3EDE9]`}>
              <div className="flex h-[8rem] flex-col justify-between 2xl:h-[9rem]">
                <div className="flex items-center justify-between">
                  <span className="text-15 font-normal leading-6 text-[#000000]">{categoryLabel}</span>
                  <span className="flex cursor-pointer font-normal leading-6 text-[#333]" onClick={(e) => e.preventDefault()}>
                    <ShopOnline itemCode={product?.variants?.[0]?.attributes?.[ITEM_CODE].toString()} />
                  </span>
                </div>
                <div className="block max-w-[80%]  md:max-w-[100%] ">
                  <p className="line-clamp-2 text-14 font-medium capitalize leading-[1.625rem] text-[#2B2C39] md:text-12 lg:text-primary-black  2xl:text-14">
                    {name}
                  </p>
                </div>
                <div className={`mt-3 flex gap-2 md:gap-0 lg:gap-0 xl:gap-0 2xl:gap-0 items-center justify-between text-11 font-medium text-[#1C1C1C] md:text-12 2xl:text-14`}>
                  <span>
                    <span className="text-[#666]">Product code</span> {productCode}
                  </span>
                </div>
                {showRrp && (
                  <div className="mt-3 flex items-center justify-between gap-2 md:gap-0 lg:gap-0 xl:gap-0 2xl:gap-0 text-11 font-medium text-[#1C1C1C] md:text-12 2xl:text-14">
                    <span className="flex items-center gap-1">
                      <span className="text-[#666]">RRP </span>
                      <span>${rrpOf(selectedVariant, locale as string)}</span>
                    </span>
                  </div>
                )}
                <div className="relative z-10 mt-3 flex w-full items-center justify-between gap-2 text-11 font-medium text-[#1C1C1C] md:text-12 2xl:text-13">
                  {product?.variants && (
                    <span className="w-full" onClick={stopLink}>
                      <ColorSwatchesDropdown
                        product={{ ...product, selectedVariant }}
                        variants={product?.variants}
                        setSelectedVariant={setSelectedVariant}
                        viewMode="list"
                      />
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* LIST
        ------------------------- */}
        {layout === 'list' && (
          <div className="flex flex-wrap lg:flex-nowrap bg-white hover:bg-gray-100 border-t border-gray-300 w-full mb-5">
            <div className="px-6 py-4 border-gray-300 text-sm text-gray-900 flex items-center gap-4 w-full lg:w-[60%]">
              <div className="flex-shrink-0 w-120 h-120 relative">
                <Image
                  priority
                  src={encodeURI(images[0].toString())}
                  alt={product.name ?? ''}
                  width={264}
                  height={264}
                  style={{ objectFit: 'contain', objectPosition: 'center' }}
                  className="rounded-sm group-hover:opacity-75"
                  sizes="64px"
                  onError={onImageError}
                />
              </div>

              <div className="flex flex-col justify-center">
                <div className="text-xs font-normal text-gray-700 leading-5 ${NationalLight.className">{categoryLabel}</div>
                <div className="text-sm font-medium text-gray-900 leading-4">{name}</div>
                <div className="mt-3 items-center justify-between text-12 font-medium text-[#1C1C1C] md:text-14 2xl:text-16">
                  <span className="hidden lg:flex ml-2">
                    {product?.variants && (
                      <span className="mr-5" onClick={stopLink}>
                        <ColorSwatchesDropdown
                          product={{ ...product, selectedVariant }}
                          variants={product?.variants}
                          setSelectedVariant={setSelectedVariant}
                          viewMode={'list'}
                        />
                      </span>
                    )}

                    <span className="flex cursor-pointer font-normal leading-6 text-[#333]" onClick={(e) => e.preventDefault()}>
                      <ShopOnline itemCode={product?.variants?.[0]?.attributes?.[ITEM_CODE].toString()} />
                    </span>
                  </span>
                </div>
              </div>
            </div>
            <div className="px-10 py-4 text-sm text-gray-900 w-full lg:w-[20%] flex items-center">
              <span className="text-[#666] mr-2 flex-shrink-0 lg:hidden">Product Code</span>
              {productCode}
            </div>

            <div className="px-10 py-4 text-sm text-gray-900 w-full lg:w-[20%] flex items-center">
              <span className="text-[#666] mr-2 flex-shrink-0 lg:hidden">RRP</span>
              {showRrp && <span>${rrpOf(selectedVariant, locale as string)}</span>}
            </div>
            <div className="w-full px-6 pb-4 lg:hidden">
              <span className="mr-5" onClick={stopLink}>
                <ColorSwatchesDropdown
                  product={{ ...product, selectedVariant }}
                  variants={product?.variants}
                  setSelectedVariant={setSelectedVariant}
                  viewMode={'list'}
                />
              </span>
            </div>
          </div>
        )}

        {/* COMPACT
        ------------------------- */}
        {layout === 'compact' && (
          <div onClick={handleTileClick}>
            <div className="relative">
              <div className="relative w-full" onMouseEnter={() => setImageHovered(true)} onMouseLeave={() => setImageHovered(false)}>
                <div className="relative block w-full xl:h-[10rem] 2xl:h-[23rem]" style={{ paddingBottom: '100%' }}>
                  <Image
                    priority
                    src={encodeURI(images[0].toString())}
                    alt={product.name ?? ''}
                    style={{ objectFit: 'cover', objectPosition: 'center' }}
                    className="w-full rounded-sm group-hover:opacity-75 "
                    fill
                    sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                    onError={onImageError}
                  />
                </div>
              </div>
            </div>

            <div className={`h-[12rem] ${cardBackground} ] 2xl:px-24 xl:px-24 lg:px-24 md:px-24  px-16  pt-16 hover:bg-[#F3EDE9]`}>
              <div className="flex h-[8rem] flex-col justify-between 2xl:h-[9rem]">
                <div className="flex items-center justify-between">
                  <span className="text-11 font-normal leading-6 text-[#333] md:text-12 2xl:text-14">{categoryLabel}</span>
                </div>

                {/* ShopOnline is NZ-only (renders nothing on AU). */}
                <div className="flex items-center justify-between">
                  <span className="flex cursor-pointer text-14 font-normal leading-6 text-[#333] md:text-12 2xl:text-14" onClick={(e) => e.preventDefault()}>
                    <ShopOnline itemCode={product?.variants?.[0]?.attributes?.[ITEM_CODE].toString()} />
                  </span>
                </div>

                <div className="block max-w-[80%]  md:max-w-[100%] ">
                  <p className="line-clamp-2 text-18 font-medium capitalize leading-[1.625rem] text-[#2B2C39] md:text-16 lg:text-primary-black  2xl:text-18">
                    {name}
                  </p>
                </div>
                {/* Stacks on very narrow screens in the renovation-guide article column. */}
                <div
                  className={`mt-4 flex gap-2 md:gap-0 lg:gap-0 xl:gap-0 2xl:gap-0  ${
                    path.includes('/renovation-guide') && width !== null && width < 361 ? 'flex-col' : 'items-center justify-between'
                  } text-12 font-medium text-[#090909] md:text-14 2xl:text-16`}
                >
                  <span>
                    <span className="text-[#666]">Product code</span> {productCode}
                  </span>
                </div>
                <div>
                  <span className="flex gap-8">
                    {product?.variants && (
                      <ColorSwatches product={product} variants={product?.variants} setSelectedVariant={setSelectedVariant} />
                    )}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}
      </NextLink>
    </div>
  );
};

export default ProductTile;
